/**
 * SPEC-TrainingSimulator — pure helpers shared by the server (serve gate,
 * grading, regrade, summaries) and the client (readiness badges, key editor).
 *
 * Nothing in here touches the DB or the network. The client and the server
 * MUST call the same functions so a readiness badge can never disagree with
 * the serve gate (§4.1) and a regrade produces what the runner would have.
 *
 *   parseAddLine        §7.1  add-box line → { kind, value }
 *   keyCompleteness     §4.1  readiness gate per phase (D14)
 *   gradeAnswer         §4.2  per-question, per-field raw grades (D23)
 *   pickTarget          §5    pace target from the keyed decision (D6)
 *   computeClock        §8.4  server-truth elapsed / remaining / state (Phase C)
 *   clockModeForPhase   §5    none | soft | extendable | hard (D5)
 *   extendAccepted / timeoutAccepted  §10  what the /extend and /timeout routes allow
 *   reviewTotals        §8.3  Phase 2 review aggregate (D18, §12 Q6)
 *   summarizeAttempt    §9    provisional attempt summary
 *   readinessByPhase / deriveItemStatus / computeServedSets   §7.1 (D21)
 *   traineeSnapshot     §6.1  strips expected/actual before a trainee sees it
 *   serveCandidates     §7.1  what a phase serves next; a retake repeats the set (Phase D)
 *   retakeCheck         §7.4  may a Senior reopen the assessment (Phases 3+4, always together)?
 *   confusionSplit      §9    false clears vs false flags, out of summary.confusion
 *   certifiableAttempt / traineeRosterSummary   §7.4, D17  roster columns + what a signature judges
 *
 * Question TYPES and BRANCHES are code (QUESTION_DEFS); question TEXT is data
 * (SimulatorQuestionConfig, seeded from DEFAULT_QUESTIONS, editable in the
 * Questions tab — D15).
 */

import { DROP_ZONE_CODES, BAD_REASONS } from "./schema";

// ─── Ids, enums ──────────────────────────────────────────────────────────────

export const QUESTION_IDS = [
  "q1_street",
  "q2_unit",
  "q3_expected_dz",
  "q4_actual_dz",
  "q5_meets",
  "q6_reference",
  "q7_return_note",
  "q8_missed_return",
  "q9_condition",
  "q10_decision",
  "q11_bad_reason",
] as const;
export type QuestionId = (typeof QUESTION_IDS)[number];

export const SIMULATOR_PHASES = [1, 2, 3, 4] as const;
export type SimulatorPhase = (typeof SIMULATOR_PHASES)[number];

export const UNCLEAR_REASONS = ["pod_missing", "cant_interpret"] as const;
export type UnclearReason = (typeof UNCLEAR_REASONS)[number];

/**
 * q7's Unclear is about the completion NOTE, not the POD, so it has its own
 * reasons (2026-09-14). "POD missing" / "Can't interpret POD" were copied
 * from q1/q2 and could not be picked honestly when a note simply said
 * nothing about returns — which left keys stuck in draft.
 */
export const Q7_UNCLEAR_REASONS = ["note_silent", "cant_read_note"] as const;
export type Q7UnclearReason = (typeof Q7_UNCLEAR_REASONS)[number];
export const Q7_UNCLEAR_LABELS: Record<Q7UnclearReason, string> = { note_silent: "Note doesn't mention returns", cant_read_note: "Can't read the note" };

export const Q10_ROWS = ["street", "unit", "dz", "returns"] as const;
export type Q10Row = (typeof Q10_ROWS)[number];

/**
 * The q10 rows a delivery asks. A delivery whose unit is N/A (q2) has no unit
 * to judge, so its "Unit correct?" row is neither keyed, shown, required nor
 * scored (2026-09-14). `key` is the answer key for the Senior, or the
 * trainee's context (their own q2, or the key's once revealed) in the rail.
 */
export function q10RowsFor(key: AnswerKey | null | undefined): readonly Q10Row[] {
  const unit = (key?.q2_unit as { value?: unknown } | null | undefined)?.value;
  return typeof unit === "string" && isNA(unit) ? Q10_ROWS.filter((r) => r !== "unit") : Q10_ROWS;
}

export const DIFFERS_VALUES = ["street", "unit", "dz"] as const;
export type DiffersValue = (typeof DIFFERS_VALUES)[number];

/** q6 "What differs?" choices for a delivery: no Unit on a unit-less (q2 = N/A) delivery (2026-09-14). */
export function differsOptionsFor(key: AnswerKey | null | undefined): readonly DiffersValue[] {
  const unit = (key?.q2_unit as { value?: unknown } | null | undefined)?.value;
  return typeof unit === "string" && isNA(unit) ? DIFFERS_VALUES.filter((d) => d !== "unit") : DIFFERS_VALUES;
}

export type YesNo = "yes" | "no";
export type YesNoUnclear = "yes" | "no" | "unclear";
/**
 * q2's "Is it visible in the POD?" also takes `na` (2026-09-14, Sarah): the
 * task has a unit but the delivery was handed over or left away from the
 * unit door (H2H, front desk, lobby…), so the photo was never going to show
 * it. Terminal — no where[], no "different unit" branch. q1 keeps Yes/No/Unclear.
 * The pill reads "Not Required" (2026-09-15, Sarah); the stored value stays `na`.
 */
export type InPodAnswer = YesNoUnclear | "na";
export const Q2_IN_POD_ANSWERS = ["yes", "no", "unclear", "na"] as const;
export const Q2_IN_POD_NA_LABEL = "Not Required";
/** q10 row values. `na` is allowed on the `unit` row only: the task has a unit but the drop zone is not at its door (front desk, lobby…), so the photos cannot confirm it (2026-09-14). */
export type RowAnswer = "yes" | "no" | "unsure" | "na";
export const ROW_ANSWERS = ["yes", "no", "unsure"] as const;
export const UNIT_ROW_ANSWERS = ["yes", "no", "unsure", "na"] as const;
export function rowAnswersFor(row: Q10Row): readonly RowAnswer[] { return row === "unit" ? UNIT_ROW_ANSWERS : ROW_ANSWERS; }
export type Decision = "GOOD" | "BAD";

/**
 * Evidence sources (D10): `photo:<1-based index>`, `note:completion`,
 * `note:destination`. Destination notes are no longer OFFERED as a choice
 * anywhere (2026-09-15, Sarah — every key ended up with the same "can't be
 * used to verify this" line against it); the value stays valid so keys and
 * answers stored before then still validate.
 */
export type EvidenceSource = string;
export const NOTE_SOURCES = ["note:completion", "note:destination"] as const;

export function isEvidenceSource(v: unknown): v is EvidenceSource {
  return typeof v === "string" && (/^photo:[1-9]\d*$/.test(v) || (NOTE_SOURCES as readonly string[]).includes(v));
}

export type QuestionType = "text" | "select" | "pill" | "decision" | "bad_reason";

/** The fixed (code-owned) shape of a question: type + branches. Not editable. */
export interface QuestionDef {
  id: QuestionId;
  type: QuestionType;
  /** Human descriptor for the read-only "what is fixed" strip in the Questions tab. */
  branches: string;
  requiredAction: string;
}

export const QUESTION_DEFS: Record<QuestionId, QuestionDef> = {
  q1_street: {
    id: "q1_street", type: "text", requiredAction: "highlighted value (Delivery Details card)",
    branches: "inPod: Yes → where[]; No → otherSeen (Yes → otherValue + where[]); Unclear → unclearReason + where[]",
  },
  q2_unit: {
    id: "q2_unit", type: "text", requiredAction: "highlighted value or N/A pill",
    branches: "same as q1; value N/A → no branch; inPod also takes Not Required (handed over / not at the unit door) → no branch",
  },
  q3_expected_dz: { id: "q3_expected_dz", type: "select", requiredAction: "pick", branches: "—" },
  q4_actual_dz: { id: "q4_actual_dz", type: "select", requiredAction: "pick + where[] (non-empty)", branches: "—" },
  q5_meets: { id: "q5_meets", type: "pill", requiredAction: "pill", branches: "No → clearWhy Yes/No; Yes → terminal" },
  q6_reference: { id: "q6_reference", type: "pill", requiredAction: "pill; Photos card switches to Verified past", branches: "Yes → conflict Yes/No (do the past photos show this delivery somewhere wrong?); conflict Yes → differs[] ⊆ {street, unit, dz} (no unit when q2 is N/A)" },
  q7_return_note: { id: "q7_return_note", type: "pill", requiredAction: "pill", branches: "Yes → quote (highlighted); No → quote optional; Unclear → note_silent | cant_read_note" },
  q8_missed_return: { id: "q8_missed_return", type: "pill", requiredAction: "pill; shown only when q7 ≠ Yes", branches: "Yes → where[]" },
  q9_condition: { id: "q9_condition", type: "pill", requiredAction: "pill", branches: "—" },
  q10_decision: { id: "q10_decision", type: "decision", requiredAction: "Yes/No/Unsure rows + Good/Bad button (mandatory)", branches: "rows: street, unit, dz, returns (no unit row when q2 is N/A; unit row also takes N/A when the drop zone is not at the unit door)" },
  q11_bad_reason: { id: "q11_bad_reason", type: "bad_reason", requiredAction: "pick — phases 3–4 only, through the real resolution panel, graded on a keyed BAD (§12 Q1)", branches: "—" },
};

// ─── Question text config (data) ─────────────────────────────────────────────

export interface QuestionTextConfig {
  prompt: string;
  labels?: Record<string, string>;
  options?: Record<string, string>;
  feedbackDefault: { correct: string; wrong: string };
}

export interface SimulatorQuestionConfig {
  id: QuestionId;
  sort: number;
  enabled: boolean;
  mandatoryPhases: number[];
  text: QuestionTextConfig;
}

const YES_NO_LABELS = { yes: "Yes", no: "No" };
const YES_NO_UNCLEAR_LABELS = { yes: "Yes", no: "No", unclear: "Unclear" };
const UNCLEAR_LABELS = { pod_missing: "POD missing", cant_interpret: "Can't interpret POD" };

export const DEFAULT_QUESTIONS: SimulatorQuestionConfig[] = [
  {
    id: "q1_street", sort: 1, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "What is the expected street number?",
      labels: { inPod: "Is it visible in the POD?", where: "Where do you see it?", otherSeen: "Do you see a different street number?", otherValue: "Which number?", unclearReason: "Why is it unclear?" },
      options: { ...YES_NO_UNCLEAR_LABELS, ...UNCLEAR_LABELS },
      feedbackDefault: { correct: "Correct — that matches the address on the task.", wrong: "Not quite. Read the street number from the task address, then check the POD photos and notes for it." },
    },
  },
  {
    id: "q2_unit", sort: 2, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "What is the expected unit?",
      labels: { na: "N/A", inPod: "Is it visible in the POD?", where: "Where do you see it?", otherSeen: "Do you see a different unit?", otherValue: "Which unit?", unclearReason: "Why is it unclear?" },
      options: { ...YES_NO_UNCLEAR_LABELS, ...UNCLEAR_LABELS, inPodNa: Q2_IN_POD_NA_LABEL },
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. The unit comes from the task address. N/A on the value means the address has no unit; Not Required on the POD question means the delivery was handed over or left away from the unit door." },
    },
  },
  {
    id: "q3_expected_dz", sort: 3, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "What is the expected drop zone?",
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. The expected drop zone comes from the destination notes; DOOR is the default when nothing else is asked for." },
    },
  },
  {
    id: "q4_actual_dz", sort: 4, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "What drop zone do you see in the POD?",
      labels: { where: "Which photos show it?" },
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. Look at where the bag actually sits in the photos, not where it should be." },
    },
  },
  {
    id: "q5_meets", sort: 5, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "Does the actual drop zone meet or exceed the expected?",
      labels: { clearWhy: "Is it clear why the driver deviated?" },
      options: YES_NO_LABELS,
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. A drop zone meets the expectation when it is at least as secure as what the customer asked for." },
    },
  },
  {
    id: "q6_reference", sort: 6, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "Does this delivery have reference photos?",
      labels: { conflict: "Do the verified past photos show this delivery at the wrong street, unit or drop zone? A past photo that simply doesn't show something is not a conflict.", differs: "Which looks wrong?" },
      options: { ...YES_NO_LABELS, street: "Street", unit: "Unit", dz: "Drop zone" },
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. Only a past photo that contradicts this delivery counts; one that does not show the unit or drop zone is not a mismatch. Check the Verified past tab of the Photos card." },
    },
  },
  {
    id: "q7_return_note", sort: 7, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "Did the driver say return packaging was collected?",
      labels: { quote: "Highlight the words in the completion note", quoteNo: "Highlight the driver's words if the note says so (optional)", unclearReason: "Why is it unclear?" },
      options: { ...YES_NO_UNCLEAR_LABELS, ...Q7_UNCLEAR_LABELS },
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. Read the driver's completion note for anything about bags or returns." },
    },
  },
  {
    id: "q8_missed_return", sort: 8, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "If not collected or unclear: do the photos suggest a missed return?",
      labels: { where: "Which photos?" },
      options: YES_NO_LABELS,
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. Look for a CookUnity bag or box left behind in the photos." },
    },
  },
  {
    id: "q9_condition", sort: 9, enabled: true, mandatoryPhases: [1, 2],
    text: {
      prompt: "Is the package in good condition?",
      options: YES_NO_LABELS,
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. Check the bag for damage, crushing, or an open seal." },
    },
  },
  {
    id: "q10_decision", sort: 10, enabled: true, mandatoryPhases: [1, 2, 3, 4],
    text: {
      prompt: "Is this delivery Good or Bad?",
      labels: { street: "Street number correct?", unit: "Unit correct?", dz: "Drop zone OK?", returns: "Returns OK?", rule: "All Yes (unit may be N/A) → Good · any No or Unsure → Bad" },
      options: { yes: "Yes", no: "No", unsure: "Unsure", na: "N/A" },
      feedbackDefault: { correct: "Correct.", wrong: "Not quite. If any row is No or Unsure the delivery is Bad." },
    },
  },
  {
    // Phase C (§12 Q1, settled 2026-09-09): asked in phases 3–4 only, through the
    // REAL AgentResolutionPanel, so the option wording is the live picklist copy
    // verbatim — a trainee meets the same five words on their first live delivery.
    id: "q11_bad_reason", sort: 11, enabled: true, mandatoryPhases: [3, 4],
    text: {
      prompt: "Why is it Bad?",
      options: {
        mistake: "Mistake — Mismatch in POD",
        cant_verify: "Can't Verify — Need More Info",
        bag_notes: "Bag Notes",
        damage: "Damage — Product Damaged",
        other: "Other",
      },
      feedbackDefault: { correct: "Correct.", wrong: "Not the reason this one is Bad." },
    },
  },
];

// ─── Pointer shapes (key-editor revision 2026-09-09, §4.1) ───────────────────

/** A bounding box on a POD photo (`target` = `photo:N`), normalised 0..1 to the photo. */
export interface PhotoBox { target: string; x: number; y: number; w: number; h: number }

export const HIGHLIGHT_FIELDS = ["company", "recipientName", "address", "taskNotes", "destinationNotes", "completionNote"] as const;
export type HighlightField = (typeof HIGHLIGHT_FIELDS)[number];
/** Anchored card text: which field, the exact substring, and which occurrence when it repeats. */
export interface TextHighlight { field: HighlightField; text: string; occurrence: number }

/** Feedback for one specific wrong choice: `[gradeField][choice] → text`. */
export type FieldRationale = Record<string, Record<string, string>>;

/**
 * Optional fields every key entry may carry. `rationale`, `fieldRationale`,
 * `boxes` and `highlights` are KEY-ONLY (never in a trainee answer, never
 * served before the question is submitted — §8.3). `valueHighlight` is the
 * one pointer the trainee's own answer also carries (q1/q2/q7): the anchor
 * captured with "Use highlighted text".
 */
export interface EntryExtras {
  rationale?: string;
  fieldRationale?: FieldRationale;
  boxes?: PhotoBox[];
  highlights?: TextHighlight[];
  valueHighlight?: TextHighlight | null;
}

/** Entry fields that exist only on the key. Stripped by `publicKeyEntry`. */
export const KEY_ONLY_FIELDS = ["rationale", "fieldRationale", "boxes", "highlights", "valueHighlight"] as const;

// ─── Answer shapes (key and trainee answers share one shape — D12) ───────────

export interface TextAnswer extends EntryExtras {
  value?: string | null;
  /** q2 also takes `na` (InPodAnswer); q1 is held to Yes/No/Unclear by `entryComplete` and the server schema. */
  inPod?: InPodAnswer;
  where?: EvidenceSource[];
  otherSeen?: YesNo;
  otherValue?: string | null;
  unclearReason?: UnclearReason;
}
export interface SelectAnswer extends EntryExtras { value?: string | null; where?: EvidenceSource[] }
/** `reason` is legacy: the "Why does it meet?" text was dropped 2026-09-15; older keys may still carry it. */
export interface MeetsAnswer extends EntryExtras { value?: YesNo; clearWhy?: YesNo; reason?: string }
/**
 * q6 (reworded 2026-09-14, Sarah): `value` = has verified past photos;
 * `conflict` = do those photos show this delivery at the wrong street, unit
 * or drop zone (Yes → `differs[]`). The old `similar` ("looks like the past
 * ones?") read partial past photos as mismatches; `normalizeReferenceEntry`
 * maps a legacy `similar: yes` to `conflict: no` and leaves a legacy
 * `similar: no` unanswered so a Senior re-judges it with the clearer question.
 */
export interface ReferenceAnswer extends EntryExtras { value?: YesNo; conflict?: YesNo; differs?: string[]; /** legacy, pre 2026-09-14 */ similar?: YesNo }

export function normalizeReferenceEntry<T extends AnswerEntry | null | undefined>(entry: T): T {
  const e = entry as any;
  if (!e || typeof e !== "object" || !("similar" in e)) return entry;
  const { similar, fieldRationale, ...rest } = e;
  const out: any = { ...rest };
  if (out.conflict === undefined && similar === "yes") out.conflict = "no";
  if (out.conflict === undefined && similar === "no") delete out.differs; // re-key: the old No did not mean a conflict
  if (fieldRationale && typeof fieldRationale === "object") {
    const { similar: simLines, ...frRest } = fieldRationale as Record<string, Record<string, string>>;
    const fr: Record<string, Record<string, string>> = { ...frRest };
    if (simLines && typeof simLines === "object") {
      // "If the trainee picks similar = yes" is now "picks conflict = no", and vice versa.
      const mapped: Record<string, string> = { ...(fr.conflict ?? {}) };
      if (typeof simLines.yes === "string" && mapped.no === undefined) mapped.no = simLines.yes;
      if (typeof simLines.no === "string" && mapped.yes === undefined) mapped.yes = simLines.no;
      if (Object.keys(mapped).length) fr.conflict = mapped;
    }
    if (Object.keys(fr).length) out.fieldRationale = fr;
  }
  return out as T;
}
export interface ReturnNoteAnswer extends EntryExtras { value?: YesNoUnclear; quote?: string; unclearReason?: Q7UnclearReason | UnclearReason }
export interface MissedReturnAnswer extends EntryExtras { value?: YesNo; where?: EvidenceSource[] }
export interface ConditionAnswer extends EntryExtras { value?: YesNo }
export interface DecisionAnswer extends EntryExtras { rows?: Partial<Record<Q10Row, RowAnswer>>; decision?: Decision }
export interface BadReasonAnswer extends EntryExtras { value?: string | null }

export type AnswerEntry =
  | TextAnswer | SelectAnswer | MeetsAnswer | ReferenceAnswer | ReturnNoteAnswer
  | MissedReturnAnswer | ConditionAnswer | DecisionAnswer | BadReasonAnswer;

export type AnswerKey = Partial<Record<QuestionId, AnswerEntry | null>>;
export type ItemAnswers = AnswerKey;

// ─── parseAddLine (§7.1) ─────────────────────────────────────────────────────

export type ParsedAddLine =
  | { kind: "onfleetTaskId"; value: string; raw: string }
  | { kind: "shortId"; value: string; raw: string }
  | { kind: "deliveryId"; value: string; raw: string }
  | { kind: "invalid"; value: null; raw: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHORT_ID_RE = /^[0-9a-f]{8}$/i;
// Onfleet long ids: 24 chars from a base64-ish alphabet incl. * and ~.
const ONFLEET_ID_RE = /^[A-Za-z0-9*~_-]{24}$/;
const TASK_PARAM_RE = /[?&#]taskId=([^&#\s]+)/;
const QAM_PATH_RE = /\/qa\/(?:agent\/)?delivery\/([0-9a-f-]{36})(?:[/?#]|$)/i;

export function parseAddLine(line: string): ParsedAddLine {
  const raw = (line ?? "").trim();
  if (!raw) return { kind: "invalid", value: null, raw };
  const taskParam = raw.match(TASK_PARAM_RE);
  if (taskParam) {
    let value = taskParam[1];
    try { value = decodeURIComponent(value); } catch { /* keep raw */ }
    return value ? { kind: "onfleetTaskId", value, raw } : { kind: "invalid", value: null, raw };
  }
  const qam = raw.match(QAM_PATH_RE);
  if (qam && UUID_RE.test(qam[1])) return { kind: "deliveryId", value: qam[1].toLowerCase(), raw };
  if (UUID_RE.test(raw)) return { kind: "deliveryId", value: raw.toLowerCase(), raw };
  if (SHORT_ID_RE.test(raw)) return { kind: "shortId", value: raw.toLowerCase(), raw };
  if (ONFLEET_ID_RE.test(raw)) return { kind: "onfleetTaskId", value: raw, raw };
  return { kind: "invalid", value: null, raw };
}

// ─── Normalisation (§4.2) ────────────────────────────────────────────────────

export function normalizeText(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Punctuation a highlight drags along from the address line — "1949," out of
 * "1949, Rue de Villiers", "(unit 4)" — never changes the value, so both the
 * street number and the unit are compared without it (2026-09-15: the first
 * trainee run graded "1949," wrong against a key of "1949"). Only the EDGES
 * are stripped; "98C", "5B" and "Ph7" keep their letters.
 */
export function stripEdgePunctuation(s: string): string {
  return s.replace(/^[\s,.;:'"()[\]#-]+/, "").replace(/[\s,.;:'"()[\]-]+$/, "");
}

/** Street number: text normalisation + edge punctuation. */
export function normalizeStreet(s: string | null | undefined): string {
  return stripEdgePunctuation(normalizeText(s));
}

/** Unit: text normalisation + strip `#`, `apt`, `apt.`, `unit` prefixes and edge punctuation. */
export function normalizeUnit(s: string | null | undefined): string {
  let v = stripEdgePunctuation(normalizeText(s));
  v = v.replace(/^(?:#|apt\.?|apartment|unit)\s*#?\s*/i, "");
  v = v.replace(/^#\s*/, "");
  return v.trim();
}

const NA_VALUES = new Set(["n/a", "na", "none"]);
export function isNA(s: string | null | undefined): boolean {
  return NA_VALUES.has(normalizeText(s));
}

function nonEmpty(s: unknown): s is string {
  return typeof s === "string" && s.trim().length > 0;
}
function isYesNo(v: unknown): v is YesNo { return v === "yes" || v === "no"; }
function isYesNoUnclear(v: unknown): v is YesNoUnclear { return v === "yes" || v === "no" || v === "unclear"; }
function isUnclearReason(v: unknown): v is UnclearReason { return (UNCLEAR_REASONS as readonly string[]).includes(v as string); }
function isQ7UnclearReason(v: unknown): v is Q7UnclearReason { return (Q7_UNCLEAR_REASONS as readonly string[]).includes(v as string); }
function isDz(v: unknown): boolean { return typeof v === "string" && (DROP_ZONE_CODES as readonly string[]).includes(v); }
function isWhereArray(v: unknown): v is EvidenceSource[] { return Array.isArray(v) && v.every(isEvidenceSource); }
function nonEmptyWhere(v: unknown): boolean { return isWhereArray(v) && v.length > 0; }

// ─── Completeness (§4.1, D14) ────────────────────────────────────────────────

/**
 * Is a single key entry complete for its question? `ctx.key` gives access to
 * sibling answers (q8 depends on q7).
 */
export function entryComplete(id: QuestionId, entry: AnswerEntry | null | undefined, ctx: { key: AnswerKey; phase?: number }): boolean {
  if (!entry) return false;
  const e = entry as any;
  switch (id) {
    case "q1_street":
    case "q2_unit": {
      if (!nonEmpty(e.value)) return false;
      // q2 keyed N/A is a terminal branch: there is no unit to look for in
      // the POD, so neither the key nor the trainee is asked `inPod` and its
      // sub-questions (2026-09-14 — N/A items used to sit in draft forever).
      if (id === "q2_unit" && isNA(e.value)) return true;
      // q2 only: the unit exists but the POD could not show it (H2H, front desk…).
      if (id === "q2_unit" && e.inPod === "na") return true;
      if (!isYesNoUnclear(e.inPod)) return false;
      if (e.inPod === "yes") return nonEmptyWhere(e.where);
      if (e.inPod === "no") {
        if (!isYesNo(e.otherSeen)) return false;
        if (e.otherSeen === "yes") return nonEmpty(e.otherValue) && nonEmptyWhere(e.where);
        return true;
      }
      return isUnclearReason(e.unclearReason) && (e.where === undefined || isWhereArray(e.where));
    }
    case "q3_expected_dz":
      return isDz(e.value);
    case "q4_actual_dz":
      return isDz(e.value) && nonEmptyWhere(e.where);
    case "q5_meets":
      if (!isYesNo(e.value)) return false;
      if (e.value === "no") return isYesNo(e.clearWhy);
      return true;
    case "q6_reference": {
      const r = normalizeReferenceEntry(e) as any;
      if (!isYesNo(r.value)) return false;
      if (r.value === "no") return true;
      if (!isYesNo(r.conflict)) return false;
      if (r.conflict === "yes") return Array.isArray(r.differs) && e.differs.length > 0 && e.differs.every((d: unknown) => (DIFFERS_VALUES as readonly string[]).includes(d as string));
      return true;
    }
    case "q7_return_note":
      if (!isYesNoUnclear(e.value)) return false;
      if (e.value === "unclear") return isQ7UnclearReason(e.unclearReason);
      // Yes must quote the driver; No may — a note that says nothing about
      // returns has nothing to highlight (2026-09-14).
      return e.value === "no" || nonEmpty(e.quote);
    case "q8_missed_return":
      if (!isYesNo(e.value)) return false;
      if (e.value === "yes") return nonEmptyWhere(e.where);
      return true;
    case "q9_condition":
      return isYesNo(e.value);
    case "q10_decision": {
      const decisionOk = e.decision === "GOOD" || e.decision === "BAD";
      // Phases 3–4 have no rail: the trainee only presses the footer Good/Bad
      // (§5), so the key needs only the decision there. The four rows are
      // asked (and therefore required) in phases 1–2 only.
      if (ctx.phase === 3 || ctx.phase === 4) return decisionOk;
      const rows = e.rows ?? {};
      const rowsOk = q10RowsFor(ctx.key).every((r) => (rowAnswersFor(r) as readonly string[]).includes(rows[r]));
      return rowsOk && decisionOk;
    }
    case "q11_bad_reason":
      return typeof e.value === "string" && (BAD_REASONS as readonly string[]).includes(e.value);
  }
  return false;
}

/**
 * Which questions the KEY asks (§4):
 *   q8  — only when the key's q7 ≠ yes;
 *   q11 — only when the key's decision is BAD (Phase C, §12 Q1: the bad reason
 *         is asked in phases 3–4 through the real resolution panel, so it is
 *         mandatory there but meaningless on a GOOD item).
 * Never read the trainee's own answers here — the rail's `asked` list and the
 * readiness gate both come through this function (Phase B audit W1).
 */
/**
 * Why a question is not asked on this delivery, in the trainee's terms — shown
 * on the Phase 2 review beside a `not_asked` row. Lives here, next to the rule
 * itself, so a new exception cannot be added without a line to explain it
 * (AUDIT-PhaseC L7; the copy used to be Q8's, hard-coded in the review).
 */
export function notAskedReason(id: QuestionId): string {
  switch (id) {
    case "q8_missed_return": return "the driver said the return packaging was collected";
    case "q11_bad_reason": return "this delivery is not Bad";
    default: return "it does not apply to this delivery";
  }
}

export function questionApplies(id: QuestionId, key: AnswerKey): boolean {
  if (id === "q8_missed_return") {
    const q7 = key.q7_return_note as ReturnNoteAnswer | null | undefined;
    return q7?.value !== "yes";
  }
  if (id === "q11_bad_reason") return keyedDecision(key) === "BAD";
  return true;
}

export function keyCompleteness(
  answerKey: AnswerKey | null | undefined,
  questionConfig: SimulatorQuestionConfig[],
  phase: number,
): { ready: boolean; missing: QuestionId[] } {
  const key = answerKey ?? {};
  const missing: QuestionId[] = [];
  const sorted = [...questionConfig].sort((a, b) => a.sort - b.sort);
  for (const q of sorted) {
    if (!q.enabled) continue;
    if (!q.mandatoryPhases.includes(phase)) continue;
    if (!questionApplies(q.id, key)) continue;
    if (!entryComplete(q.id, key[q.id] ?? null, { key, phase })) missing.push(q.id);
  }
  return { ready: missing.length === 0, missing };
}

export type PhaseReadiness = Record<SimulatorPhase, { ready: boolean; missing: QuestionId[] }>;

export function readinessByPhase(answerKey: AnswerKey | null | undefined, questionConfig: SimulatorQuestionConfig[]): PhaseReadiness {
  return {
    1: keyCompleteness(answerKey, questionConfig, 1),
    2: keyCompleteness(answerKey, questionConfig, 2),
    3: keyCompleteness(answerKey, questionConfig, 3),
    4: keyCompleteness(answerKey, questionConfig, 4),
  };
}

export type ItemStatus = "draft" | "ready" | "retired";

/** §7.2 — status is `ready` when every eligible phase is complete; retired sticks. */
export function deriveItemStatus(
  item: { status: string; eligiblePhases: number[]; answerKey: AnswerKey | null | undefined },
  questionConfig: SimulatorQuestionConfig[],
): ItemStatus {
  if (item.status === "retired") return "retired";
  if (!item.eligiblePhases.length) return "draft";
  const allReady = item.eligiblePhases.every((p) => keyCompleteness(item.answerKey, questionConfig, p).ready);
  return allReady ? "ready" : "draft";
}

// ─── Served sets (§7.1, D21) ─────────────────────────────────────────────────

export interface ServedSetItem {
  id: string;
  sequence: number | null;
  inSet: boolean;
  eligiblePhases: number[];
  answerKey: AnswerKey | null | undefined;
  status: string;
}
export interface PhaseServedSet {
  required: number;
  readyInSet: number;
  servedIds: string[];
  overflowIds: string[];
  state: "short" | "ok" | "over";
}
export type PhaseSizes = Record<number, number>;

export function computeServedSets(
  items: ServedSetItem[],
  questionConfig: SimulatorQuestionConfig[],
  phaseSizes: PhaseSizes,
): Record<SimulatorPhase, PhaseServedSet> {
  const out = {} as Record<SimulatorPhase, PhaseServedSet>;
  for (const phase of SIMULATOR_PHASES) {
    const required = phaseSizes[phase] ?? 0;
    const eligible = items
      .filter((i) => i.status !== "retired" && i.inSet && i.eligiblePhases.includes(phase))
      .filter((i) => keyCompleteness(i.answerKey, questionConfig, phase).ready)
      .sort((a, b) => {
        const sa = a.sequence ?? Number.MAX_SAFE_INTEGER;
        const sb = b.sequence ?? Number.MAX_SAFE_INTEGER;
        return sa === sb ? a.id.localeCompare(b.id) : sa - sb;
      });
    const servedIds = eligible.slice(0, required).map((i) => i.id);
    const overflowIds = eligible.slice(required).map((i) => i.id);
    const readyInSet = eligible.length;
    out[phase] = {
      required,
      readyInSet,
      servedIds,
      overflowIds,
      state: readyInSet < required ? "short" : readyInSet > required ? "over" : "ok",
    };
  }
  return out;
}

// ─── Grading (§4.2, D23) ─────────────────────────────────────────────────────

export type FieldGrade = "correct" | "wrong" | "no_answer" | "not_scored";
export interface QuestionGrade {
  status: "correct" | "wrong" | "no_answer";
  fields: Record<string, FieldGrade>;
}

function eq(a: unknown, b: unknown): FieldGrade {
  if (a === undefined || a === null || a === "") return "no_answer";
  return a === b ? "correct" : "wrong";
}

function gradeWhere(keyWhere: unknown, ansWhere: unknown): FieldGrade {
  const k = isWhereArray(keyWhere) ? keyWhere : [];
  const a = isWhereArray(ansWhere) ? ansWhere : [];
  if (k.length === 0) return a.length === 0 ? "correct" : "wrong";
  if (a.length === 0) return "wrong";
  const ks = new Set(k);
  return a.every((s) => ks.has(s)) ? "correct" : "wrong";
}

function gradeQuote(keyQuote: unknown, ansQuote: unknown): FieldGrade {
  const k = normalizeText(typeof keyQuote === "string" ? keyQuote : "");
  const a = normalizeText(typeof ansQuote === "string" ? ansQuote : "");
  if (!a) return "no_answer";
  if (!k) return "wrong";
  return a.includes(k) || k.includes(a) ? "correct" : "wrong";
}

function gradeTextValue(id: QuestionId, keyValue: unknown, ansValue: unknown): FieldGrade {
  const kv = typeof keyValue === "string" ? keyValue : "";
  const av = typeof ansValue === "string" ? ansValue : "";
  // A submitted answer with a blank value is wrong, not unanswered — "N/A
  // must be explicit" (spec §12 open question 3, current rule).
  if (!av.trim()) return "wrong";
  if (id === "q2_unit") {
    const kna = isNA(kv);
    const ana = isNA(av);
    if (kna || ana) return kna && ana ? "correct" : "wrong";
    return normalizeUnit(kv) === normalizeUnit(av) ? "correct" : "wrong";
  }
  return normalizeStreet(kv) === normalizeStreet(av) ? "correct" : "wrong";
}

function finish(fields: Record<string, FieldGrade>): QuestionGrade {
  const scored = Object.values(fields).filter((f) => f !== "not_scored");
  if (scored.length === 0) return { status: "no_answer", fields };
  if (scored.every((f) => f === "no_answer")) return { status: "no_answer", fields };
  return { status: scored.every((f) => f === "correct") ? "correct" : "wrong", fields };
}

/** Grade one question. `key` is the keyed entry; `answer` the trainee's (null → no_answer). */
export function gradeAnswer(id: QuestionId, key: AnswerEntry | null | undefined, answer: AnswerEntry | null | undefined, ctx?: { key?: AnswerKey | null }): QuestionGrade {
  let k = (key ?? {}) as any;
  // q10: a unit-less delivery (key q2 = N/A) carries no "Unit correct?" row,
  // even if a stale one was saved before q2 was set to N/A.
  if (id === "q10_decision" && ctx?.key && !q10RowsFor(ctx.key).includes("unit") && k.rows && "unit" in k.rows) {
    const { unit: _unit, ...rows } = k.rows;
    k = { ...k, rows };
  }
  const a = (answer ?? null) as any;
  if (a === null) {
    // Every scoreable field of the keyed branch is a no_answer.
    const fields: Record<string, FieldGrade> = {};
    if (id === "q10_decision") {
      // Only rows the key carries (a phase 3–4 key has none; an N/A-unit key has no unit row).
      const kr = k.rows ?? {};
      for (const r of Q10_ROWS) fields[`rows.${r}`] = kr[r] === undefined ? "not_scored" : "no_answer";
      fields.decision = "no_answer";
    } else {
      fields.value = "no_answer";
    }
    return { status: "no_answer", fields };
  }
  const fields: Record<string, FieldGrade> = {};
  switch (id) {
    case "q1_street":
    case "q2_unit": {
      // The highlighted value (street number, unit) is RECORDED but never
      // SCORED (Sarah, 2026-09-15): a highlight is too easy to get slightly
      // wrong — a dragged-in comma, a dropped letter — for it to count against
      // a trainee. What IS scored is the judgement around it: inPod and its
      // branch. The one exception is q2's N/A pill, an explicit "no unit"
      // choice, which is still graded against the key.
      const naBranch = id === "q2_unit" && (isNA(k.value) || isNA(a.value));
      fields.value = naBranch ? gradeTextValue(id, k.value, a.value) : "not_scored";
      // q2 N/A is a branch of its own (§4.2): a key with no unit asks nothing
      // else, and a trainee who picked N/A was asked nothing else, so `inPod`
      // and its sub-fields are scored only when both sides named a unit.
      if (naBranch) return finish(fields);
      fields.inPod = eq(a.inPod, k.inPod);
      const sameBranch = a.inPod === k.inPod;
      if (k.inPod === "yes") {
        fields.where = sameBranch ? gradeWhere(k.where, a.where) : "not_scored";
      } else if (k.inPod === "no") {
        fields.otherSeen = sameBranch ? eq(a.otherSeen, k.otherSeen) : "not_scored";
        if (k.otherSeen === "yes") {
          const sameSub = sameBranch && a.otherSeen === "yes";
          fields.otherValue = "not_scored"; // highlighted text, recorded only (2026-09-15)
          fields.where = sameSub ? gradeWhere(k.where, a.where) : "not_scored";
        }
      } else if (k.inPod === "unclear") {
        fields.unclearReason = sameBranch ? eq(a.unclearReason, k.unclearReason) : "not_scored";
        fields.where = sameBranch ? gradeWhere(k.where, a.where) : "not_scored";
      }
      return finish(fields);
    }
    case "q3_expected_dz":
      fields.value = eq(a.value, k.value);
      return finish(fields);
    case "q4_actual_dz":
      fields.value = eq(a.value, k.value);
      fields.where = gradeWhere(k.where, a.where);
      return finish(fields);
    case "q5_meets": {
      fields.value = eq(a.value, k.value);
      if (k.value === "no") fields.clearWhy = a.value === "no" ? eq(a.clearWhy, k.clearWhy) : "not_scored";
      return finish(fields);
    }
    case "q6_reference": {
      k = normalizeReferenceEntry(k);
      const ra = normalizeReferenceEntry(a) as any;
      fields.value = eq(ra.value, k.value);
      if (k.value === "yes") {
        const same = ra.value === "yes";
        fields.conflict = same ? eq(ra.conflict, k.conflict) : "not_scored";
        if (k.conflict === "yes") {
          const sameSub = same && ra.conflict === "yes";
          if (!sameSub) fields.differs = "not_scored";
          else {
            // A unit-less delivery (key q2 = N/A) has no Unit to differ on: drop
            // it from both sides so a stale keyed "unit" or a trainee's stray
            // pick neither scores nor penalises (2026-09-14).
            const allowed = new Set<string>(differsOptionsFor(ctx?.key));
            const kd = Array.from(new Set<string>(Array.isArray(k.differs) ? k.differs : [])).filter((d) => allowed.has(d)).sort();
            const ad = Array.from(new Set<string>(Array.isArray(ra.differs) ? ra.differs : [])).filter((d) => allowed.has(d)).sort();
            fields.differs = ad.length === 0 ? "no_answer" : kd.join(",") === ad.join(",") ? "correct" : "wrong";
          }
        }
      }
      return finish(fields);
    }
    case "q7_return_note": {
      fields.value = eq(a.value, k.value);
      const same = a.value === k.value;
      if (k.value === "unclear") fields.unclearReason = same ? eq(a.unclearReason, k.unclearReason) : "not_scored";
      // The highlighted quote is recorded for the Senior but never scored
      // (Sarah, 2026-09-15) — the same rule as the q1/q2 value.
      else fields.quote = "not_scored";
      return finish(fields);
    }
    case "q8_missed_return": {
      fields.value = eq(a.value, k.value);
      if (k.value === "yes") fields.where = a.value === "yes" ? gradeWhere(k.where, a.where) : "not_scored";
      return finish(fields);
    }
    case "q9_condition":
      fields.value = eq(a.value, k.value);
      return finish(fields);
    case "q10_decision": {
      const kr = k.rows ?? {};
      const ar = a.rows ?? {};
      // Rows are scored only when the key has them (phases 3–4 keys carry a
      // decision alone, and the real footer submits no rows).
      for (const r of Q10_ROWS) fields[`rows.${r}`] = kr[r] === undefined ? "not_scored" : eq(ar[r], kr[r]);
      fields.decision = eq(a.decision, k.decision);
      return finish(fields);
    }
    case "q11_bad_reason":
      fields.value = eq(a.value, k.value);
      return finish(fields);
  }
  return finish(fields);
}

/** Grade every listed question of one item. Missing answers → no_answer. */
export function gradeItem(
  key: AnswerKey | null | undefined,
  answers: ItemAnswers | null | undefined,
  questionIds: readonly QuestionId[],
): Record<QuestionId, QuestionGrade> {
  const out = {} as Record<QuestionId, QuestionGrade>;
  for (const id of questionIds) out[id] = gradeAnswer(id, key?.[id] ?? null, answers?.[id] ?? null, { key });
  return out;
}

/** Question ids that are graded for a phase: enabled + mandatory in that phase + applies given the key. */
export function gradedQuestionIds(key: AnswerKey | null | undefined, questionConfig: SimulatorQuestionConfig[], phase: number): QuestionId[] {
  return [...questionConfig]
    .sort((a, b) => a.sort - b.sort)
    .filter((q) => q.enabled && q.mandatoryPhases.includes(phase) && questionApplies(q.id, key ?? {}))
    .map((q) => q.id);
}

export function keyedDecision(key: AnswerKey | null | undefined): Decision | null {
  const d = (key?.q10_decision as DecisionAnswer | null | undefined)?.decision;
  return d === "GOOD" || d === "BAD" ? d : null;
}

// ─── Key facets — Training set filters (2026-09-15, Sarah) ───────────────────
//
// One flat, enumerable view of an answer key, so a Senior can pull up every
// delivery keyed a certain way ("all BAD", "return packaging not collected",
// "unit row unsure") and read the keys side by side for consistency. Pure:
// GET /items serialises it per row and the Training set table filters on it.
// `null` means that field is not keyed (or not asked — a unit row on an N/A
// unit, a bad reason on a GOOD key).

export interface KeyFacets {
  decision: string | null;
  badReason: string | null;
  streetInPod: string | null;
  /** "na" when the address has no unit, "set" when the key holds one. */
  unit: string | null;
  unitInPod: string | null;
  expectedDz: string | null;
  actualDz: string | null;
  meets: string | null;
  clearWhy: string | null;
  reference: string | null;
  conflict: string | null;
  returnNote: string | null;
  missedReturn: string | null;
  condition: string | null;
  rowStreet: string | null;
  rowUnit: string | null;
  rowDz: string | null;
  rowReturns: string | null;
}
export type KeyFacetId = keyof KeyFacets;

const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

export function keyFacets(key: AnswerKey | null | undefined): KeyFacets {
  const k = key ?? {};
  const q1 = k.q1_street as TextAnswer | null | undefined;
  const q2 = k.q2_unit as TextAnswer | null | undefined;
  const q5 = k.q5_meets as MeetsAnswer | null | undefined;
  const q6 = normalizeReferenceEntry(k.q6_reference as ReferenceAnswer | null | undefined);
  const q7 = k.q7_return_note as ReturnNoteAnswer | null | undefined;
  const q8 = k.q8_missed_return as MissedReturnAnswer | null | undefined;
  const q9 = k.q9_condition as ConditionAnswer | null | undefined;
  const q10 = k.q10_decision as DecisionAnswer | null | undefined;
  const q11 = k.q11_bad_reason as BadReasonAnswer | null | undefined;
  const decision = keyedDecision(k);
  const unitNa = typeof q2?.value === "string" && isNA(q2.value);
  const rows = q10?.rows ?? {};
  return {
    decision,
    badReason: decision === "BAD" ? str(q11?.value) : null,
    streetInPod: str(q1?.inPod),
    unit: q2?.value == null || q2.value === "" ? null : unitNa ? "na" : "set",
    unitInPod: unitNa ? null : str(q2?.inPod),
    expectedDz: str((k.q3_expected_dz as SelectAnswer | null | undefined)?.value),
    actualDz: str((k.q4_actual_dz as SelectAnswer | null | undefined)?.value),
    meets: str(q5?.value),
    clearWhy: q5?.value === "no" ? str(q5?.clearWhy) : null,
    reference: str(q6?.value),
    conflict: q6?.value === "yes" ? str(q6?.conflict) : null,
    returnNote: str(q7?.value),
    missedReturn: str(q8?.value),
    condition: str(q9?.value),
    rowStreet: str(rows.street),
    rowUnit: unitNa ? null : str(rows.unit),
    rowDz: str(rows.dz),
    rowReturns: str(rows.returns),
  };
}

export const PHASE_LABELS: Record<number, string> = { 0: "Orientation", 1: "Learn", 2: "Practice", 3: "Normal QA", 4: "Strict" };

export interface ItemFilterCtx {
  /** Phases whose SERVED set (in set ∧ eligible ∧ ready, within `phaseSizes`) holds this item. */
  servedPhases: number[];
}
export interface ItemFilterOption { value: string; label: string; match: (f: KeyFacets, ctx: ItemFilterCtx) => boolean }
export interface ItemFilterDef { id: string; label: string; options: ItemFilterOption[] }

const BAD_REASON_LABELS = (DEFAULT_QUESTIONS.find((q) => q.id === "q11_bad_reason")?.text.options ?? {}) as Record<string, string>;
const notConfirmed = (v: string | null) => v === "no" || v === "unsure";

/**
 * The six Training set filters (Sarah, 2026-09-15). Each dropdown is one
 * def; picking an option keeps the rows whose `match` is true. Filters AND
 * together. "Missing" means the Q10 row is keyed No or Unsure — the photos do
 * not confirm that field — so it only ever matches a key with Q10 rows
 * (Phases 1–2 keys; a Phase 3–4-only key carries just the decision).
 */
export const ITEM_FILTER_DEFS: ItemFilterDef[] = [
  {
    id: "phase", label: "Phase",
    options: [
      ...SIMULATOR_PHASES.map((p) => ({ value: String(p), label: `Phase ${p} · ${PHASE_LABELS[p]}`, match: (_f: KeyFacets, ctx: ItemFilterCtx) => ctx.servedPhases.includes(p) })),
      { value: "bench", label: "Bench (not served)", match: (_f, ctx) => ctx.servedPhases.length === 0 },
    ],
  },
  {
    id: "decision", label: "Decision",
    options: [
      { value: "GOOD", label: "GOOD", match: (f) => f.decision === "GOOD" },
      { value: "BAD", label: "BAD", match: (f) => f.decision === "BAD" },
      { value: "none", label: "Not keyed", match: (f) => f.decision === null },
    ],
  },
  {
    id: "returns", label: "Return collection",
    options: [
      { value: "collected", label: "Collected", match: (f) => f.returnNote === "yes" },
      { value: "not_collected", label: "Not collected", match: (f) => f.returnNote === "no" },
      { value: "unclear", label: "Unclear", match: (f) => f.returnNote === "unclear" },
      { value: "missed", label: "Missed return", match: (f) => f.missedReturn === "yes" },
    ],
  },
  {
    id: "conflict", label: "Past photos",
    options: [
      { value: "conflict", label: "Conflict with past photos", match: (f) => f.conflict === "yes" },
      { value: "no_conflict", label: "No conflict", match: (f) => f.reference === "yes" && f.conflict === "no" },
      { value: "none", label: "No past photos", match: (f) => f.reference === "no" },
    ],
  },
  {
    id: "missing", label: "Missing in POD",
    options: [
      { value: "street", label: "Street number", match: (f) => notConfirmed(f.rowStreet) },
      { value: "unit", label: "Unit number", match: (f) => notConfirmed(f.rowUnit) },
      { value: "dz", label: "Drop zone", match: (f) => notConfirmed(f.rowDz) },
    ],
  },
  {
    id: "expectedDz", label: "Expected drop zone",
    options: DROP_ZONE_CODES.map((c) => ({ value: c, label: c, match: (f: KeyFacets) => f.expectedDz === c })),
  },
];

/** Does an item pass every active filter? `filters` maps def id → option value ("" / absent = any). */
export function itemMatchesFilters(f: KeyFacets, ctx: ItemFilterCtx, filters: Record<string, string | undefined>): boolean {
  for (const def of ITEM_FILTER_DEFS) {
    const want = filters[def.id];
    if (!want) continue;
    const opt = def.options.find((o) => o.value === want);
    if (!opt || !opt.match(f, ctx)) return false; // an unknown option value matches nothing
  }
  return true;
}

/** One line per row in the Training set table — the same facts the filters use, only where keyed. */
export function describeKeyFacets(f: KeyFacets): string {
  const parts: string[] = [];
  if (f.badReason) parts.push(BAD_REASON_LABELS[f.badReason] ?? f.badReason);
  if (f.expectedDz) parts.push(`expects ${f.expectedDz}${f.actualDz && f.actualDz !== f.expectedDz ? ` → ${f.actualDz}` : ""}`);
  if (f.returnNote === "yes") parts.push("return collected");
  else if (f.returnNote === "no") parts.push("return not collected");
  else if (f.returnNote === "unclear") parts.push("return unclear");
  if (f.missedReturn === "yes") parts.push("missed return");
  if (f.conflict === "yes") parts.push("past-photo conflict");
  else if (f.reference === "no") parts.push("no past photos");
  const missing = [notConfirmed(f.rowStreet) && "street", notConfirmed(f.rowUnit) && "unit", notConfirmed(f.rowDz) && "drop zone"].filter(Boolean);
  if (missing.length) parts.push(`missing ${missing.join(", ")}`);
  return parts.join(" · ");
}

// ─── Settings (§5.1) ─────────────────────────────────────────────────────────

export interface SimulatorSettings {
  targetGoodSeconds: number;
  targetOtherSeconds: number;
  warningSeconds: number;
  extendSeconds: number;
  phase2Enforced: boolean;
  passAccuracyPct: number;
  passPacePct: number;
  phaseSizes: PhaseSizes;
}

/**
 * The warning threshold the LIVE `TimerChip` compiles in
 * (`TIMER_WARNING_S` in `client/src/lib/agent-ui-helpers.ts`). The runner mounts
 * that chip unchanged (§5), so `simulator.warningSeconds` cannot differ from it
 * without the red chip and the "+ Time" button disagreeing on screen — verified
 * at 25, where the button was offered beside a calm purple chip for eight
 * seconds (AUDIT-PhaseC M3). `PUT /settings` refuses any other value; a lint in
 * `train-runner-structure.test.ts` pins the two constants together.
 */
export const LIVE_CHIP_WARNING_SECONDS = 15;

export const SIMULATOR_SETTING_DEFAULTS: SimulatorSettings = {
  targetGoodSeconds: 20,
  targetOtherSeconds: 45,
  warningSeconds: LIVE_CHIP_WARNING_SECONDS,
  extendSeconds: 30,
  phase2Enforced: false,
  passAccuracyPct: 90,
  passPacePct: 80,
  phaseSizes: { 1: 10, 2: 20, 3: 20, 4: 15 },
};

/** `app_settings` key for a settings field: `simulator.<field>`. */
export const SIMULATOR_SETTING_KEY_PREFIX = "simulator.";
export const SIMULATOR_SETTING_FIELDS = Object.keys(SIMULATOR_SETTING_DEFAULTS) as Array<keyof SimulatorSettings>;

export function pickTarget(keyed: Decision | null | undefined, settings: Pick<SimulatorSettings, "targetGoodSeconds" | "targetOtherSeconds">): number {
  return keyed === "GOOD" ? settings.targetGoodSeconds : settings.targetOtherSeconds;
}

// ─── Clocks (§5 D5, §8.4 — Phase C) ──────────────────────────────────────────

/**
 * What the clock DOES on a phase:
 *   none        Phase 1 — no chip at all.
 *   soft        Phase 2 — shown and recorded, never blocks (D5).
 *   extendable  Phase 3 — "+ Time" adds `extendSeconds` per press; at 0:00 the
 *               chip reads 0:00 and the item STAYS OPEN (§8.4), overrun scores
 *               as over target.
 *   hard        Phase 4 — at 0:00 the item auto-advances, timed_out, no decision.
 * `phase2Enforced` (§5.1) promotes Phase 2 to the Phase 3 behaviour.
 */
export type ClockMode = "none" | "soft" | "extendable" | "hard";
/** Display tier. `warning` is the "+ Time" window; `expired` is at or past 0:00. */
export type ClockState = "none" | "healthy" | "warning" | "expired";

export function clockModeForPhase(phase: number, settings: Pick<SimulatorSettings, "phase2Enforced">): ClockMode {
  if (phase <= 1) return "none";
  if (phase === 2) return settings.phase2Enforced ? "extendable" : "soft";
  if (phase === 3) return "extendable";
  return "hard";
}

export interface ClockReading {
  mode: ClockMode;
  /** Countdown start before any extension (§5: always `targetOtherSeconds`). */
  targetSeconds: number;
  /** The window in which "+ Time" is offered and the chip turns warning (§5.1). */
  warningSeconds: number;
  /** Countdown start plus every granted extension. */
  allowedSeconds: number;
  /** Server truth: whole seconds since `opened_at` (§8.4). Never negative. */
  elapsedSeconds: number;
  /** Clamped at 0 — what the chip counts down. */
  remainingSeconds: number;
  /** Seconds past 0:00; 0 while healthy. Phase 3 keeps running here. */
  overrunSeconds: number;
  state: ClockState;
  expired: boolean;
  /** "+ Time" is offered: an extendable clock in its warning (or overrun) window. */
  canExtend: boolean;
}

const asMs = (v: string | Date | null | undefined): number | null => {
  if (v == null) return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * The one place elapsed / remaining time is decided. The client renders what
 * this returns and ticks its own copy for display only (§8.4) — every accept
 * decision (extend, timeout, over-target) recomputes it server-side from
 * `openedAt`, so a paused tab, a closed laptop or a hand-edited clock cannot
 * buy time.
 */
export function computeClock(input: {
  phase: number;
  openedAt: string | Date | null | undefined;
  now: string | Date;
  /** Sum of the seconds granted by `simulator_time_events` (kind = extend). */
  extendedSeconds?: number;
  settings: Pick<SimulatorSettings, "targetOtherSeconds" | "warningSeconds" | "extendSeconds" | "phase2Enforced">;
}): ClockReading {
  const mode = clockModeForPhase(input.phase, input.settings);
  const granted = Math.max(0, Math.round(input.extendedSeconds ?? 0));
  const targetSeconds = Math.max(0, Math.round(input.settings.targetOtherSeconds));
  const warningSeconds = Math.max(0, Math.round(input.settings.warningSeconds));
  const allowedSeconds = targetSeconds + granted;
  const openedMs = asMs(input.openedAt);
  const nowMs = asMs(input.now) ?? 0;
  const elapsedSeconds = openedMs == null ? 0 : Math.max(0, Math.floor((nowMs - openedMs) / 1000));
  const remainingSeconds = Math.max(0, allowedSeconds - elapsedSeconds);
  const overrunSeconds = Math.max(0, elapsedSeconds - allowedSeconds);
  const expired = openedMs != null && elapsedSeconds >= allowedSeconds;
  const state: ClockState =
    mode === "none" ? "none"
      : expired ? "expired"
      : remainingSeconds <= warningSeconds ? "warning"
      : "healthy";
  return {
    mode, targetSeconds, warningSeconds, allowedSeconds, elapsedSeconds, remainingSeconds, overrunSeconds, expired, state,
    // "+ Time" is on offer for the WHOLE item on an extendable clock (Sarah,
    // 2026-09-15) — it used to appear only inside the warning window.
    canExtend: mode === "extendable",
  };
}

/** POST …/extend is accepted only on an extendable clock (§10 — Phase 3, or Phase 2 when enforced). */
export function extendAccepted(reading: ClockReading): boolean {
  return reading.mode === "extendable";
}

/**
 * POST …/timeout is accepted only on a HARD clock that has actually run out
 * (§10: "server validates elapsed ≥ allowed before accepting"). A client that
 * posts early is refused and keeps working.
 */
export function timeoutAccepted(reading: ClockReading): boolean {
  return reading.mode === "hard" && reading.expired;
}

// ─── Phase 2 review screen (§8.3, D18 — Phase C) ─────────────────────────────

/** One question row on the review screen. `not_asked` questions are listed but never counted. */
export type ReviewStatus = "correct" | "wrong" | "no_answer" | "not_asked";

/**
 * Grand total + per-item totals for the review screen. Sarah's call (§12 Q6,
 * 2026-09-09): Phase 2 SHOWS an aggregate — "42 of 60 correct" plus a per-item
 * count — because Phase 2 is the practice phase and D7's no-score rule is
 * about the graded phases 3–4. Only graded questions count, so a `not_asked`
 * Q8 never makes an item look worse than the one beside it.
 */
export function reviewTotals(items: Array<{ questions: Array<{ status: ReviewStatus }> }>): {
  correct: number;
  graded: number;
  perItem: Array<{ correct: number; graded: number }>;
} {
  const perItem = items.map((it) => {
    const scored = it.questions.filter((q) => q.status !== "not_asked");
    return { correct: scored.filter((q) => q.status === "correct").length, graded: scored.length };
  });
  return {
    correct: perItem.reduce((n, i) => n + i.correct, 0),
    graded: perItem.reduce((n, i) => n + i.graded, 0),
    perItem,
  };
}

// ─── Attempt summary (§9, provisional — D23) ─────────────────────────────────

export interface AttemptAnswerRow {
  grades: Record<string, QuestionGrade> | null;
  keyedDecision: Decision | null;
  decision: Decision | null;
  elapsedMs: number | null;
  timedOut: boolean;
  extensions: { count: number; seconds: number };
  badReasonCorrect: boolean | null;
}

export interface AttemptSummary {
  phase: number;
  items: number;
  accuracyPct: number;
  badReasonPct: number | null;
  confusion: Record<string, number>;
  medianElapsedMs: number | null;
  withinTargetPct: number;
  extensions: { count: number; totalSeconds: number };
  timeouts: number;
  passRecommended: boolean | null;
  computedAt: string;
}

function pct(num: number, den: number): number {
  return den === 0 ? 0 : Math.round((num / den) * 1000) / 10;
}

export function summarizeAttempt(phase: number, rows: AttemptAnswerRow[], settings: SimulatorSettings): AttemptSummary {
  let accuracyPct = 0;
  if (phase <= 2) {
    let graded = 0;
    let correct = 0;
    for (const r of rows) {
      for (const g of Object.values(r.grades ?? {})) {
        graded += 1;
        if (g.status === "correct") correct += 1;
      }
    }
    accuracyPct = pct(correct, graded);
  } else {
    const match = rows.filter((r) => r.decision != null && r.decision === r.keyedDecision).length;
    accuracyPct = pct(match, rows.length);
  }

  const keyedBad = rows.filter((r) => r.keyedDecision === "BAD");
  const badReasonPct = keyedBad.length === 0 ? null : pct(keyedBad.filter((r) => r.badReasonCorrect === true).length, keyedBad.length);

  const confusion: Record<string, number> = {};
  for (const r of rows) {
    const k = `${r.keyedDecision ?? "none"}→${r.decision ?? "none"}`;
    confusion[k] = (confusion[k] ?? 0) + 1;
  }

  const elapsed = rows.map((r) => r.elapsedMs).filter((e): e is number => typeof e === "number" && Number.isFinite(e)).sort((a, b) => a - b);
  let medianElapsedMs: number | null = null;
  if (elapsed.length) {
    const mid = Math.floor(elapsed.length / 2);
    medianElapsedMs = elapsed.length % 2 === 1 ? elapsed[mid] : (elapsed[mid - 1] + elapsed[mid]) / 2;
  }

  const within = rows.filter((r) => !r.timedOut && typeof r.elapsedMs === "number" && r.elapsedMs <= pickTarget(r.keyedDecision, settings) * 1000).length;
  const withinTargetPct = pct(within, rows.length);

  const extensions = rows.reduce(
    (acc, r) => ({ count: acc.count + (r.extensions?.count ?? 0), totalSeconds: acc.totalSeconds + (r.extensions?.seconds ?? 0) }),
    { count: 0, totalSeconds: 0 },
  );
  const timeouts = rows.filter((r) => r.timedOut).length;

  const passRecommended = phase === 4
    ? accuracyPct >= settings.passAccuracyPct && withinTargetPct >= settings.passPacePct
    : null;

  return {
    phase, items: rows.length, accuracyPct, badReasonPct, confusion, medianElapsedMs,
    withinTargetPct, extensions, timeouts, passRecommended, computedAt: new Date().toISOString(),
  };
}

// ─── Snapshot (§6.1) ─────────────────────────────────────────────────────────

export interface SimulatorSnapshotPhoto { index: number; photoId: string; url: string }
export interface SimulatorSnapshotVerifiedPast {
  completedAt: string | null;
  dropZone: string | null;
  photoUrl: string | null;
  verifiedBy: string | null;
}
export interface SimulatorSnapshot {
  customer: string | null;
  recipientName: string | null;
  address: string | null;
  destinationNotes: string | null;
  taskNotes: string | null;
  taskType: "pickup" | "delivery" | null;
  driverName: string | null;
  completedAt: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  scanId: string | null;
  driverCompletionNotes: string | null;
  onfleetTaskId: string | null;
  shortId: string | null;
  /** Key-editor only — never sent to a trainee. */
  expected: { street: string | null; unit: string | null; dz: string | null };
  /** Key-editor only — never sent to a trainee. */
  actual: { street: string | null; unit: string | null; dz: string | null };
  podPhotos: SimulatorSnapshotPhoto[];
  verifiedPast: SimulatorSnapshotVerifiedPast[];
  verifiedPastCount: number;
  capturedAt: string;
}

export type TraineeSnapshot = Omit<SimulatorSnapshot, "expected" | "actual">;

const TRAINEE_STRIP_RE = /^(expected|actual|ai|bert|yolo|scene|baseline|ocr|pdr)([A-Z_]|$)/;

/** Drop every model-derived field before the snapshot reaches a trainee (§6.1 agent payload rule). */
export function traineeSnapshot(snapshot: SimulatorSnapshot | Record<string, unknown>): TraineeSnapshot {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(snapshot ?? {})) {
    if (TRAINEE_STRIP_RE.test(k)) continue;
    if (/(Confidence|_confidence|_trace|Trace|_fingerprint|Fingerprint)$/.test(k)) continue;
    out[k] = v;
  }
  return out as TraineeSnapshot;
}

// ─── Lessons (§7.3a, D19) ────────────────────────────────────────────────────

export const LESSON_POSITIONS = ["start", "before_item", "on_first_bad"] as const;
export type LessonPosition = (typeof LESSON_POSITIONS)[number];
export interface LessonPlacement { phase: 0 | 1 | 2 | 3 | 4; position: LessonPosition; itemIndex?: number }
export const LESSON_MAX_IMAGES = 6;
export const LESSON_MAX_IMAGE_BYTES = 500 * 1024;

/** Validate a placement per §7.3a: phase 0 only `start`; `before_item` needs itemIndex; `on_first_bad` only phases 3–4. */
export function validateLessonPlacement(p: unknown): string | null {
  if (!p || typeof p !== "object") return "placement required";
  const { phase, position, itemIndex } = p as Record<string, unknown>;
  if (![0, 1, 2, 3, 4].includes(phase as number)) return "phase must be 0–4";
  if (!(LESSON_POSITIONS as readonly string[]).includes(position as string)) return "position must be start | before_item | on_first_bad";
  if (phase === 0 && position !== "start") return "phase 0 lessons can only be placed at start";
  if (position === "before_item" && !(Number.isInteger(itemIndex) && (itemIndex as number) >= 0)) return "before_item needs an itemIndex ≥ 0";
  if (position === "on_first_bad" && phase !== 3 && phase !== 4) return "on_first_bad is only for phases 3–4";
  return null;
}

/** Approximate decoded byte size of a data URI (or 0 for an https URL). */
export function dataUriBytes(src: string): number {
  if (!src.startsWith("data:")) return 0;
  const comma = src.indexOf(",");
  if (comma === -1) return src.length;
  const payload = src.slice(comma + 1);
  if (/;base64/i.test(src.slice(0, comma))) return Math.floor((payload.length * 3) / 4);
  return payload.length;
}

// ─── Trainee runner (Phase B, §8) ────────────────────────────────────────────


/** The keyed entry as the trainee may see it after submitting: answer fields only. */
export function publicKeyEntry(entry: AnswerEntry | null | undefined): AnswerEntry | null {
  if (!entry || typeof entry !== "object") return null;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(entry)) {
    if ((KEY_ONLY_FIELDS as readonly string[]).includes(k)) continue;
    if (v === undefined) continue;
    out[k] = v;
  }
  return out as AnswerEntry;
}

export interface QuestionReveal {
  keyed: AnswerEntry | null;
  boxes: PhotoBox[];
  highlights: TextHighlight[];
  valueHighlight: TextHighlight | null;
}

/** What the runner reveals for ONE question once it has been submitted (§8.3). */
export function revealForQuestion(key: AnswerKey | null | undefined, id: QuestionId): QuestionReveal {
  const e = (key?.[id] ?? null) as (AnswerEntry & EntryExtras) | null;
  const vh = e?.valueHighlight;
  return {
    keyed: publicKeyEntry(e),
    boxes: Array.isArray(e?.boxes) ? e!.boxes! : [],
    highlights: Array.isArray(e?.highlights) ? e!.highlights! : [],
    valueHighlight: vh && typeof vh.text === "string" ? vh : null,
  };
}

export interface FeedbackResolution {
  text: string;
  source: "correct" | "choice" | "rationale" | "default";
  field?: string;
  choice?: string;
}

const SET_VALUED_FIELDS = new Set(["where", "differs"]);

function readPath(obj: unknown, field: string): unknown {
  let cur: any = obj;
  for (const part of field.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[part];
  }
  return cur;
}

/**
 * §4.1 feedback for a graded question: for every wrong field, in grade order,
 * `fieldRationale[field][choice]` where the choice is the option the trainee
 * picked (set-valued fields: each selected value not in the key, in selection
 * order; q2's N/A on `value`); else the entry's `rationale`; else the
 * question's default `wrong` text. A correct grade returns the `correct` text.
 */
export function resolveFeedback(
  id: QuestionId,
  keyEntry: AnswerEntry | null | undefined,
  answer: AnswerEntry | null | undefined,
  grade: QuestionGrade,
  config: SimulatorQuestionConfig,
): FeedbackResolution {
  if (grade.status === "correct") return { text: config.text.feedbackDefault.correct, source: "correct" };
  const k = (id === "q6_reference" ? normalizeReferenceEntry(keyEntry ?? {}) : (keyEntry ?? {})) as any;
  const a = (id === "q6_reference" ? normalizeReferenceEntry(answer ?? {}) : (answer ?? {})) as any;
  const fr: FieldRationale = k.fieldRationale && typeof k.fieldRationale === "object" ? k.fieldRationale : {};
  for (const [field, status] of Object.entries(grade.fields)) {
    if (status !== "wrong" && status !== "no_answer") continue;
    const lines = fr[field];
    if (!lines) continue;
    const choices: string[] = [];
    if (SET_VALUED_FIELDS.has(field)) {
      const keyed = new Set<string>(Array.isArray(k[field]) ? k[field] : []);
      for (const c of Array.isArray(a[field]) ? a[field] : []) if (typeof c === "string" && !keyed.has(c)) choices.push(c);
    } else if (field === "value" && id === "q2_unit") {
      if (isNA(typeof a.value === "string" ? a.value : "")) choices.push("N/A");
    } else {
      const v = readPath(a, field);
      if (typeof v === "string") choices.push(v);
    }
    for (const c of choices) {
      const text = lines[c];
      if (typeof text === "string" && text.trim()) return { text, source: "choice", field, choice: c };
    }
  }
  if (typeof k.rationale === "string" && k.rationale.trim()) return { text: k.rationale, source: "rationale" };
  return { text: config.text.feedbackDefault.wrong, source: "default" };
}

/** Does the current (draft) entry expose a `where[]` field — i.e. should the Photos card be in pick mode? */
export function whereFieldActive(id: QuestionId, entry: AnswerEntry | null | undefined): boolean {
  const e = (entry ?? {}) as any;
  switch (id) {
    case "q1_street":
    case "q2_unit":
      if (id === "q2_unit" && isNA(e.value)) return false;
      return e.inPod === "yes" || e.inPod === "unclear" || (e.inPod === "no" && e.otherSeen === "yes");
    case "q4_actual_dz":
      return true;
    case "q8_missed_return":
      return e.value === "yes";
    default:
      return false;
  }
}

// ─── Home cards (§8.1) ───────────────────────────────────────────────────────

export type PhaseCardState = "locked" | "available" | "in_progress" | "complete";
export interface PhaseCard {
  phase: 0 | 1 | 2 | 3 | 4;
  label: string;
  state: PhaseCardState;
  lockedReason: string | null;
  progress: { done: number; total: number };
  attemptId: string | null;
}
export interface PhaseCardsInput {
  phaseSizes: PhaseSizes;
  /** Ready in-set items per phase (computeServedSets(...)[p].readyInSet). */
  readyInSet: Record<number, number>;
  /**
   * The user's attempts (any phase may be absent). The highest `attemptNo` per
   * phase decides its card — required, not optional, because the server's lock
   * guard picks the same way and the two must not be able to drift (audit D1).
   */
  attempts: Array<{ id: string; phase: number; status: string; decidedCount: number; attemptNo: number }>;
  phase0: { read: number; total: number };
  /** Phases the runner can serve today (Phase B: [1]). Others read "Coming soon" once reachable. */
  builtPhases: number[];
}

export const LOCK_REASON_SHORT_SET = "Not available yet — ask your Senior";
export const LOCK_REASON_COMING_SOON = "Coming soon";

export function phaseCards(input: PhaseCardsInput): PhaseCard[] {
  const cards: PhaseCard[] = [];
  const p0Total = input.phase0.total;
  const p0Done = Math.min(input.phase0.read, p0Total);
  const p0Complete = p0Done >= p0Total;
  cards.push({
    phase: 0, label: PHASE_LABELS[0], state: p0Complete ? "complete" : p0Done > 0 ? "in_progress" : "available",
    lockedReason: null, progress: { done: p0Done, total: p0Total }, attemptId: null,
  });
  let previousComplete = p0Complete;
  for (const phase of SIMULATOR_PHASES) {
    const total = input.phaseSizes[phase] ?? 0;
    // The LATEST attempt decides the card. A retake (attempt n+1, in_progress)
    // therefore puts a finished phase back to "in progress" on its own — which
    // is exactly the blind reopen D8 asks for: the card becomes startable again
    // and says nothing about how the last attempt went (Sarah, 2026-09-10).
    // Highest attempt_no, not last-in-array: the server's own lock guard picks the
    // same way, and relying on caller order made the two disagree whenever the
    // rows had not come back in started_at order (AUDIT-Final D1).
    const mine = input.attempts.filter((a) => a.phase === phase);
    const attempt = mine.length ? mine.reduce((best, a) => (a.attemptNo > best.attemptNo ? a : best)) : null;
    // The chain is strictly linear (Sarah, 2026-09-10): while phase p-1 is not
    // complete, phase p is locked whatever its OWN attempts say. Reopening a
    // phase as a retake therefore re-locks every phase after it until the
    // retake is finished, including one the trainee had already completed.
    let card: PhaseCard;
    if (!previousComplete) {
      card = {
        phase, label: PHASE_LABELS[phase], state: "locked",
        lockedReason: phase === 1 ? "Read the Phase 0 orientation first" : `Finish Phase ${phase - 1} first`,
        progress: { done: attempt && attempt.status === "complete" ? total : (attempt?.decidedCount ?? 0), total },
        attemptId: null,
      };
    } else if (attempt && attempt.status === "complete") {
      card = { phase, label: PHASE_LABELS[phase], state: "complete", lockedReason: null, progress: { done: total, total }, attemptId: attempt.id };
    } else if (attempt && attempt.status === "in_progress") {
      card = { phase, label: PHASE_LABELS[phase], state: "in_progress", lockedReason: null, progress: { done: attempt.decidedCount, total }, attemptId: attempt.id };
    } else {
      let lockedReason: string | null = null;
      if (!input.builtPhases.includes(phase)) lockedReason = LOCK_REASON_COMING_SOON;
      else if ((input.readyInSet[phase] ?? 0) < total) lockedReason = LOCK_REASON_SHORT_SET;
      card = { phase, label: PHASE_LABELS[phase], state: lockedReason ? "locked" : "available", lockedReason, progress: { done: 0, total }, attemptId: null };
    }
    cards.push(card);
    previousComplete = card.state === "complete";
  }
  return cards;
}

// ─── Lesson sequencing (§8.3b) ───────────────────────────────────────────────

export interface LessonLike { id: string; placement: unknown; enabled: boolean; sort: number }
export interface LessonReadLike { lessonId: string; readAt: Date | string }

/**
 * The lesson to show before serving the next item, or null.
 *   - `start` lessons: before the first item of the attempt, in sort order,
 *     once per attempt (a read older than the attempt does not count — start
 *     lessons always show again on a retake).
 *   - `before_item n`: right before item index n, once ever.
 */
export function nextLesson<L extends LessonLike>(
  lessons: L[],
  reads: LessonReadLike[],
  ctx: { phase: number; itemIndex: number; itemsServed: number; attemptStartedAt: Date },
): L | null {
  const readAt = new Map<string, number>();
  for (const r of reads) {
    const t = new Date(r.readAt).getTime();
    const prev = readAt.get(r.lessonId);
    if (prev === undefined || t > prev) readAt.set(r.lessonId, t);
  }
  const sorted = lessons
    .filter((l) => l.enabled && (l.placement as LessonPlacement).phase === ctx.phase)
    .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
  if (ctx.itemsServed === 0) {
    const since = ctx.attemptStartedAt.getTime();
    for (const l of sorted) {
      if ((l.placement as LessonPlacement).position !== "start") continue;
      const t = readAt.get(l.id);
      if (t === undefined || t < since) return l;
    }
  }
  for (const l of sorted) {
    const pl = l.placement as LessonPlacement;
    if (pl.position !== "before_item" || pl.itemIndex !== ctx.itemIndex) continue;
    if (!readAt.has(l.id)) return l;
  }
  return null;
}

// ─── Phase D — retakes, roster, certification (§7.4, §9, D8, D13, D17) ───────
//
// Decisions taken with Sarah on 2026-09-10, before Phase D was built. Each one
// changes what these helpers may assume, so they are written down here as well
// as in SPEC §11 "Phase D":
//
//   · A retake RE-SERVES the phase's served set, in sequence, whatever the
//     trainee has already seen. D13 ("an item reaches a trainee at most once")
//     is amended to "at most once PER ATTEMPT" — the bench does not backfill.
//   · A retake tells the trainee NOTHING: the phase card simply becomes
//     startable again. No score, no reason, no attempt number (D7, D8).
//   · Reopening phase p RE-LOCKS p+1..4 until the retake completes, so the
//     sequence stays strictly linear.
//   · Certification judges the LATEST complete Phase 4 attempt.
//   · There is NO automatic verdict. `passRecommended` keeps computing so a
//     later session can weight it (D23, §12 Q7 still open), but no Senior-
//     facing surface displays it — the Senior reads the raw performance and
//     signs Pass / Fail themselves, or opens a retake instead of signing.

/**
 * The order a phase's items are served in for one attempt, and which of them
 * are still to come.
 *
 * Attempt 1 keeps D13: an item this trainee met in ANY other attempt is
 * skipped, so the fixed set (D3) is never repeated. A retake (attempt ≥ 2)
 * ignores `seenElsewhere` entirely and walks the phase's served set from the
 * top, skipping only what this attempt has already answered — that is what
 * makes a retake the same length as the attempt it repeats.
 */
export function serveCandidates(input: {
  servedIds: string[];
  /** Items already answered inside THIS attempt. */
  seenHere: Iterable<string>;
  /** Items answered in any OTHER attempt by the same trainee. */
  seenElsewhere: Iterable<string>;
  attemptNo: number;
}): string[] {
  const skip = new Set<string>(Array.from(input.seenHere));
  if (input.attemptNo <= 1) for (const id of Array.from(input.seenElsewhere)) skip.add(id);
  return input.servedIds.filter((id) => !skip.has(id));
}

// ─── Retake eligibility (§7.4, §10 POST /trainees/:userId/retake) ────────────

export interface RetakeAttemptLike { id: string; phase: number; attemptNo: number; status: string }

/** The phases a retake reopens, always together (see `retakeCheck`). */
export const ASSESSMENT_PHASES = [3, 4] as const;

export interface RetakeCheck {
  ok: boolean;
  /** Why the assessment cannot be reopened; null when `ok`. Shown on the button. */
  reason: string | null;
  /** The attempt_no both new rows take. The two phases stay in lockstep. */
  nextAttemptNo: number;
  /** The phases reopened together. */
  phases: number[];
}

/**
 * May a Senior open a retake of `phase` for this trainee?
 *
 * A retake repeats something already done, so the phase must have a COMPLETE
 * attempt; and two open attempts on one phase would race for `attempt_no`, so
 * an in-progress one blocks it. `locks` is what the dialog warns about: the
 * later phases the trainee cannot touch until the retake is finished.
 */
/**
 * May a Senior reopen the assessment for this trainee?
 *
 * **A retake is the assessment — Phases 3 and 4, always together, never
 * separately** (Sarah, 2026-09-10, after the Phase D audit). One action creates
 * Phase 3 attempt *n* and Phase 4 attempt *n* as an atomic pair, so the two
 * attempt numbers stay in lockstep and the Senior scores the pair as a unit.
 *
 * Phases 1 and 2 are never retaken: both reveal the key as the trainee works
 * (per question in Phase 1, on the review screen after Phase 2), so a repeat
 * could not be blind (D8). A trainee may only *review* that work.
 *
 * Two conditions, and they are the whole rule:
 *   1. Phase 4 has a complete attempt — there is something to reopen.
 *   2. The trainee has **no attempt in progress in any phase**. This is what
 *      keeps a running clock from being stranded behind a freshly locked card
 *      (audit M3), and it is why the pair can be created safely.
 * Plus a readiness gate: neither phase may be short of `phaseSizes`, or the
 * reopened run would finish early on a null candidate (audit L1 / Phase B W10).
 *
 * `nextAttemptNo` is the highest attempt number across BOTH phases plus one, so
 * a historic imbalance cannot desynchronise the pair.
 */
export function retakeCheck(
  attempts: RetakeAttemptLike[],
  opts: {
    /** Ready in-set items per phase (computeServedSets(...)[p].readyInSet). */
    readyInSet?: Record<number, number>;
    phaseSizes?: PhaseSizes;
    builtPhases?: number[];
  } = {},
): RetakeCheck {
  const built = opts.builtPhases ?? [...SIMULATOR_PHASES];
  const phases: number[] = [...ASSESSMENT_PHASES];
  const mine = attempts.filter((a) => phases.includes(a.phase));
  const nextAttemptNo = mine.reduce((m, a) => Math.max(m, a.attemptNo), 0) + 1;
  const base = { nextAttemptNo, phases };

  for (const p of phases) {
    if (!built.includes(p)) return { ok: false, reason: `Phase ${p} is not built yet`, ...base };
  }
  const open = attempts.find((a) => a.status === "in_progress");
  if (open) {
    return { ok: false, reason: `Phase ${open.phase} has an attempt in progress — it must be finished before the assessment is reopened`, ...base };
  }
  if (!attempts.some((a) => a.phase === 4 && a.status === "complete")) {
    return { ok: false, reason: "Phase 4 has not been completed yet — there is no assessment to reopen", ...base };
  }
  if (opts.readyInSet && opts.phaseSizes) {
    for (const p of phases) {
      const ready = opts.readyInSet[p] ?? 0;
      const need = opts.phaseSizes[p] ?? 0;
      if (ready < need) return { ok: false, reason: `Phase ${p} has only ${ready} of ${need} ready deliveries in the set`, ...base };
    }
  }
  return { ok: true, reason: null, ...base };
}

// ─── Certification (§7.4, D17, §10 POST /trainees/:userId/certify) ───────────

export const CERTIFICATION_RESULTS = ["pass", "fail"] as const;
export type CertificationResult = (typeof CERTIFICATION_RESULTS)[number];

export interface CertifiableAttemptLike { id: string; phase: number; attemptNo: number; status: string; finishedAt?: Date | string | null }

/**
 * The Phase 4 attempt a certification signs off: the LATEST complete one
 * (Sarah, 2026-09-10). Retakes are genuine second chances, so the most recent
 * complete run is the one judged; the roster still shows attempt 1 beside it.
 */
export function certifiableAttempt<A extends CertifiableAttemptLike>(attempts: A[]): A | null {
  const done = attempts.filter((a) => a.phase === 4 && a.status === "complete");
  if (!done.length) return null;
  return done.reduce((best, a) => (a.attemptNo > best.attemptNo ? a : best));
}

// ─── Roster row (§7.4 columns) ──────────────────────────────────────────────

export interface RosterAttemptLike {
  id: string;
  phase: number;
  attemptNo: number;
  status: string;
  decidedCount: number;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  summary: AttemptSummary | null;
}
export interface RosterCertificationLike {
  id: string;
  attemptId: string;
  result: string;
  signedAt: Date | string | null;
  signedByName: string | null;
  note: string | null;
}

/** One Phase 4 run as the roster shows it. No verdict — the numbers only. */
export interface RosterScore {
  attemptId: string;
  attemptNo: number;
  accuracyPct: number;
  withinTargetPct: number;
  badReasonPct: number | null;
  timeouts: number;
  items: number;
  /**
   * The two ways of being wrong, split out of `summary.confusion` (audit M6).
   *
   * §9 and §12 Q7 both promise the Senior this on the roster, and it is the
   * whole reason Q7 is still open: a delivery keyed BAD that the trainee called
   * GOOD is waved through and reaches the customer, while a keyed GOOD called
   * BAD costs a second look and nothing else. `accuracyPct` counts them alike,
   * so the split is what a Senior needs in order to sign without one.
   */
  falseClears: number;
  falseFlags: number;
  noDecision: number;
}

/**
 * Read the keyed×given decision pairs `summarizeAttempt` stores (§9) into the
 * three counts the roster shows. Keys are `"<keyed>→<given>"` with "none" on
 * either side, so this is a lookup, not a recount — a regrade that rewrites the
 * summary flows straight through (D22).
 */
export function confusionSplit(confusion: Record<string, number> | null | undefined): { falseClears: number; falseFlags: number; noDecision: number } {
  const c = confusion ?? {};
  const n = (k: string) => c[k] ?? 0;
  return {
    falseClears: n("BAD→GOOD"),
    falseFlags: n("GOOD→BAD"),
    noDecision: n("BAD→none") + n("GOOD→none") + n("none→none"),
  };
}

export interface TraineeRosterSummary {
  /**
   * Where the trainee is now — the LOWEST phase whose latest attempt is not
   * complete, i.e. the phase their own home page offers them.
   *
   * Not the most recently created attempt (audit M5): reopening the assessment
   * inserts the Phase 4 row milliseconds after the Phase 3 row, so a newest-row
   * rule made the roster read "Phase 4" for every trainee who is actually on
   * Phase 3 and cannot even reach Phase 4 yet.
   */
  currentPhase: number | null;
  currentStatus: "not_started" | "in_progress" | "complete";
  currentAttemptNo: number | null;
  currentProgress: { done: number; total: number | null };
  /** Phase 4, attempt 1 — the unaided run (D8 "first attempt reported separately"). */
  phase4First: RosterScore | null;
  /** Phase 4, latest complete run — what a certification judges. */
  phase4Latest: RosterScore | null;
  /** Every attempt's "+ Time" presses added up (§9 extensions, Phase C data). */
  extensions: { count: number; totalSeconds: number };
  timeouts: number;
  certification: { result: string; signedAt: string | null; signedByName: string | null; attemptNo: number | null; note: string | null } | null;
  /** Phases with an open retake (an in-progress attempt numbered 2 or higher). */
  retakesOpen: number[];
  /**
   * True when `certify` would be accepted: a complete Phase 4 attempt exists AND
   * no assessment is open. Reopening the assessment is declining to sign
   * (Sarah, 2026-09-10), so the panel and the route agree through this flag.
   */
  certifiable: boolean;
  /**
   * The signature on the roster judged an OLDER attempt than `phase4Latest`
   * (audit M2) — the trainee has run the assessment again since it was signed
   * and that run is unjudged. Null when there is no certification.
   */
  certificationStale: boolean | null;
}

const iso = (v: Date | string | null | undefined): string | null => (v ? new Date(v).toISOString() : null);

function rosterScore(a: RosterAttemptLike | null): RosterScore | null {
  if (!a || !a.summary) return null;
  const s = a.summary;
  return {
    attemptId: a.id,
    attemptNo: a.attemptNo,
    accuracyPct: s.accuracyPct,
    withinTargetPct: s.withinTargetPct,
    badReasonPct: s.badReasonPct,
    timeouts: s.timeouts,
    items: s.items,
    ...confusionSplit(s.confusion),
  };
}

/**
 * The at-a-glance columns of §7.4, derived from what is already stored: the
 * per-attempt `summary` (§9) and the certification rows. Nothing is recomputed
 * from raw grades here, so a regrade (D22) that rewrites a summary flows
 * straight through to the roster.
 *
 * `passRecommended` is deliberately NOT read (Sarah, 2026-09-10): the roster
 * reports performance and the Senior signs the verdict.
 */
export function traineeRosterSummary(
  attempts: RosterAttemptLike[],
  certifications: RosterCertificationLike[],
  phaseSizes?: PhaseSizes,
): TraineeRosterSummary {
  // "Now" follows the same chain the trainee's home does: the lowest phase whose
  // latest attempt is unfinished, else the highest phase they have completed
  // (audit M5 — a newest-row rule names Phase 4 the instant an assessment opens).
  const latestOf = (phase: number) => {
    const rows = attempts.filter((a) => a.phase === phase);
    if (!rows.length) return null;
    return rows.reduce((best, a) => (a.attemptNo > best.attemptNo ? a : best));
  };
  const perPhase = SIMULATOR_PHASES.map((p) => latestOf(p));
  const current =
    perPhase.find((a) => a != null && a.status !== "complete") ??
    [...perPhase].reverse().find((a) => a != null) ??
    null;

  const p4 = attempts.filter((a) => a.phase === 4);
  const first = p4.filter((a) => a.attemptNo === 1 && a.status === "complete")[0] ?? null;
  const latestComplete = certifiableAttempt(p4.map((a) => ({ ...a })));
  const latest = latestComplete ? (attempts.find((a) => a.id === latestComplete.id) ?? null) : null;

  const extensions = attempts.reduce(
    (acc, a) => ({
      count: acc.count + (a.summary?.extensions.count ?? 0),
      totalSeconds: acc.totalSeconds + (a.summary?.extensions.totalSeconds ?? 0),
    }),
    { count: 0, totalSeconds: 0 },
  );
  const timeouts = attempts.reduce((n, a) => n + (a.summary?.timeouts ?? 0), 0);

  const signed = [...certifications].sort((a, b) => {
    const ta = a.signedAt ? new Date(a.signedAt).getTime() : 0;
    const tb = b.signedAt ? new Date(b.signedAt).getTime() : 0;
    return ta - tb;
  });
  const cert = signed[signed.length - 1] ?? null;
  const certAttempt = cert ? (attempts.find((a) => a.id === cert.attemptId) ?? null) : null;
  const assessmentOpen = attempts.some((a) => ASSESSMENT_PHASES.includes(a.phase as 3 | 4) && a.status === "in_progress");

  return {
    currentPhase: current?.phase ?? null,
    currentStatus: !current ? "not_started" : current.status === "complete" ? "complete" : "in_progress",
    currentAttemptNo: current?.attemptNo ?? null,
    currentProgress: { done: current?.decidedCount ?? 0, total: current ? (phaseSizes?.[current.phase] ?? null) : null },
    phase4First: rosterScore(first),
    phase4Latest: rosterScore(latest),
    extensions,
    timeouts,
    certification: cert
      ? { result: cert.result, signedAt: iso(cert.signedAt), signedByName: cert.signedByName, attemptNo: certAttempt?.attemptNo ?? null, note: cert.note }
      : null,
    certificationStale: cert ? (latest != null && cert.attemptId !== latest.id) : null,
    retakesOpen: Array.from(new Set(attempts.filter((a) => a.status === "in_progress" && a.attemptNo > 1).map((a) => a.phase))).sort((x, y) => x - y),
    certifiable: latest != null && !assessmentOpen,
  };
}
